import { Spin as Loader } from 'antd';
import type { Argument } from 'classnames';
import classNames from 'classnames';
import React from 'react';

import { MEDIA_TYPE, STATUS_FAILED, STATUS_LOADING } from 'constants/MediaConstants';

import type { MediaGalleryActions, MediaListItem } from '../../types';
import { DeleteIcon, ItemWrapper, MediaWrapper } from '../UI';

import Document from './Document';
import FailedMedia from './FailedMedia';
import Image from './Image';
import PendingMedia from './PendingMedia';
import Video from './Videos';

type RenderableMediaProps = {
  media: MediaListItem;
} & MediaGalleryActions;

// Each media component narrows `media` further than the dispatch can express
// (e.g. MediaListItem<ImageMedia>); the mapping key guarantees the runtime match.
const mapping = {
  [MEDIA_TYPE.IMAGE]: Image,
  [MEDIA_TYPE.IMAGE_INSIDE_360]: Image,
  [MEDIA_TYPE.DOCUMENT]: Document,
  [MEDIA_TYPE.VIDEO]: Video,
  [MEDIA_TYPE[360]]: Video
} as Record<string, React.ComponentType<RenderableMediaProps>>;

type MediaComponentProps = {
  media: MediaListItem;
  hideQuickDelete?: boolean;
  className?: Argument;
} & MediaGalleryActions;

const Media: React.FC<MediaComponentProps> = ({ media, onRestrict, onRetry, hideQuickDelete, className, ...actions }) => {
  const isLoading = media.status === STATUS_LOADING;
  const haveFailed = media.status === STATUS_FAILED;
  const hasContent = Boolean(media.data?.thumbnailPath || media.data?.path);
  const RenderComponent = mapping[media.data.type];
  const isPendingFile = !hasContent && !haveFailed;
  const canRenderChild = !isLoading && !haveFailed && !isPendingFile && RenderComponent !== undefined;

  const handleOnRetry = React.useCallback(() => {
    onRetry?.(media);
  }, [onRetry, media]);

  const handleQuickDelete = React.useCallback(() => {
    actions.onDelete?.(media);
  }, [actions, media]);

  return (
    <MediaWrapper data-cy={`advice-media-${media.data.id}`} className={classNames('media-item', className)}>
      {isLoading && <ItemWrapper><Loader /></ItemWrapper>}
      {haveFailed && <FailedMedia media={media} onRetry={handleOnRetry} onDelete={actions.onDelete} />}
      {!hideQuickDelete && <DeleteIcon className="fb-icon--cross-circle" onClick={handleQuickDelete} data-cy="quick-delete" />}
      {canRenderChild && (
        <RenderComponent media={media} onRestrict={onRestrict} {...actions} />
      )}
      {isPendingFile && !isLoading && <PendingMedia deviceId={media.data.deviceId} media={media} {...actions} />}
    </MediaWrapper>
  );
};

export default Media;
